import classNames from "classnames";
import { Key } from "client/Misc/commonTypes";
import {
	ensureNotNil,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import {
	ChangeEvent,
	ReactElement,
	useContext,
	FocusEvent,
	useCallback,
	KeyboardEvent,
	useLayoutEffect,
	useRef,
	Ref,
	forwardRef,
	useImperativeHandle,
	SyntheticEvent
} from "react";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { UniqueInputIdContext } from "../../Contexts/UniqueInputIdContext";
import {
	ControlAttachmentContext,
	ControlAttachmentOptions,
	getAttachmentClassNames
} from "../ControlAttachment";
import styles from "./TextInput.module.less";
import { never } from "client_legacy/ndaw/errors";
import { InputLike } from "../InputLike/InputLike";
import { useCallbackWithoutArguments } from "client/Hooks";

export type TextInputHandle = {
	focus: () => void;
	blur: () => void;
};

export type KeyEventArguments = {
	key: Key | null;
	modifiers: {
		shift: boolean;
		capsLock: boolean;
	};
};

export type TextInputProps = {
	type?: "text" | "password" | "email" | "tel";
	value: string | null;
	placeholder?: string;
	isInvalid?: boolean;
	attachedOn?: ControlAttachmentOptions;
	isBorderless?: boolean;
	hasNoPadding?: boolean;
	height?: "auto" | "fill";
	autoFocus?: "focus" | "select";
	onFocus?(): void;
	onBlur?(value: string): void;
	onKeyDown?: (args: KeyEventArguments) => boolean;
	onClick?: () => void;
	onSelectionChange?: (from: number, to: number) => void;
	shouldBlurOnEscape?: boolean;
	inputMode?: "decimal" | "numeric";
	autoCapitalize?: "none" | "sentences" | "words" | "characters";
	trackingId?: string;
} & ({ onChange(value: string): void; isDisabled?: boolean; } | { isDisabled: true; onChange?(value: string): void; });

export const TextInput = forwardRef(
	(props: TextInputProps, ref: Ref<TextInputHandle>): ReactElement => {
		const {
			type = "text",
			value,
			onChange,
			placeholder,
			isDisabled = false,
			isInvalid = false,
			attachedOn = [],
			isBorderless = false,
			hasNoPadding = false,
			height = "auto",
			autoFocus,
			shouldBlurOnEscape = false,
			onFocus,
			onBlur,
			onKeyDown,
			onSelectionChange,
			inputMode,
			autoCapitalize,
			trackingId,
			onClick: onClickFromProps
		} = props;

		const onClick: (() => void) | undefined = useCallbackWithoutArguments(onClickFromProps);
		const { id } = useContext(UniqueInputIdContext);
		const {
			attachedOn: attachedOnFromContext
		} = useContext(ControlAttachmentContext);
		const nativeRef: Ref<HTMLInputElement> = useRef(null);
		const onValueChange = (args: ChangeEvent<HTMLInputElement>): void => {
			if (!onChange) {
				return;
			}

			const newValue: string = args.target.value;
			onChange(newValue);
		};

		const onBlurInvocator = (args: FocusEvent<HTMLInputElement>): void => {
			if (!onBlur) {
				return;
			}

			const newValue: string = args.target.value;
			onBlur(newValue);
		};

		const keydownHandler = useCallback(
			(event: KeyboardEvent<HTMLInputElement>): void => {
				if (!onKeyDown) {
					return;
				}

				const key: Key | null = tryResolveKeyboardKey(event);

				const result: boolean = onKeyDown({
					key,
					modifiers: {
						shift: event.shiftKey,
						capsLock: event.getModifierState("CapsLock")
					}
				});

				if (result) {
					event.preventDefault();
				}
			},
			[onKeyDown]
		);

		const selectHandler = (args: SyntheticEvent<HTMLInputElement, Event>): void => {
			if (!onSelectionChange) {
				return;
			}

			const target: EventTarget = args.target;

			if (!(target instanceof HTMLInputElement)) {
				return;
			}

			onSelectionChange(
				ensureNotNil(target.selectionStart),
				ensureNotNil(target.selectionEnd)
			);
		};

		const blurOnEscapeHandler = useCallback(
			(e: KeyboardEvent<HTMLInputElement>) => {
				if (tryResolveKeyboardKey(e) === Key.Escape) {
					e.currentTarget.blur();
				}
			},
			[]
		);

		useLayoutEffect(
			() => {
				if (nativeRef.current && isNotNil(autoFocus)) {
					switch (autoFocus) {
						case "focus":
							nativeRef.current.focus();
							break;
						case "select":
							nativeRef.current.select();
							break;
						default:
							never(autoFocus);
					}
				}
			},
			[autoFocus]
		);

		useImperativeHandle(
			ref,
			(): TextInputHandle => ({
				focus: (): void => {
					nativeRef.current
						?.focus();
				},
				blur: (): void => {
					nativeRef.current
						?.blur();
				}
			}),
			[]
		);

		return (
			<input
				{...setTrackingId(trackingId)}
				ref={nativeRef}
				id={id}
				type={type}
				className={classNames(
					styles.root,
					{
						[styles.root_isInvalid]: isInvalid,
						[styles.root_borderless]: isBorderless,
						[styles.root_noPadding]: hasNoPadding,
						[styles.root_fillHeight]: height === "fill"
					},
					getAttachmentClassNames(attachedOn, attachedOnFromContext)
				)}
				value={value ?? ""}
				onChange={onValueChange}
				onFocus={onFocus}
				onBlur={onBlurInvocator}
				placeholder={placeholder}
				disabled={isDisabled}
				onKeyDown={keydownHandler}
				onSelect={selectHandler}
				onKeyUp={shouldBlurOnEscape ? blurOnEscapeHandler : undefined}
				inputMode={inputMode}
				autoCapitalize={autoCapitalize}
				onClick={onClick}
			/>
		);
	}
);
TextInput.displayName = "TextInput";

export type TextInputReadOnlyProps = {
	value: string | null;
	trackingId?: string;
};

export const TextInputReadOnly = (props: TextInputReadOnlyProps): ReactElement => {
	const {
		value,
		trackingId
	} = props;

	const { id } = useContext(UniqueInputIdContext);

	return (
		<>
			<InputLike
				isBorderless
				hasNoPadding
				value={value}
				trackingId={trackingId}
			/>
			{isNotNilOrEmpty(id) && (
				<input
					id={id}
					type="text"
					disabled
					className={styles.readonlyHiddenInput}
					value={value ?? undefined}
				/>
			)}

		</>
	);
};
TextInputReadOnly.displayName = "TextInputReadOnly";
